import { createAsyncThunk } from "@reduxjs/toolkit";
import { API_ENDPOINTS } from "../../constants/apiConstants";
import { AxiosError } from "axios";
import { iApiError, iThunkApi } from "../../types/commonTypes";
import { DEFAULT_API_ERROR } from "../../constants/commonConstants";
import { API_METHOD_TYPE, apiManager, isApiSuccess } from "../apiManager";
import {
	IAdminVendorChatDetails,
	IArchiveVendorChatsResponse,
	IChatsData,
	IGetAllChatsParams,
	IUnarchiveVendorChatsResponse,
	IVendorChatTarget,
} from "../../interfaces/chats";
import { createLatestRequest } from "./latestRequest";

function buildChatsListQueryKey(params?: IGetAllChatsParams): string {
	const queryParams = new URLSearchParams();
	queryParams.append("sortBy", "lastActivity");

	if (params) {
		if (params.archived) queryParams.append("archived", "true");
		if (params.page) queryParams.append("page", params.page.toString());
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.search) queryParams.append("search", params.search);
		if (params.orderId) queryParams.append("orderId", params.orderId);
		if (params.eventDateStart) queryParams.append("eventDateStart", params.eventDateStart);
		if (params.eventDateEnd) queryParams.append("eventDateEnd", params.eventDateEnd);
		if (params.createdDateStart) queryParams.append("createdDateStart", params.createdDateStart);
		if (params.createdDateEnd) queryParams.append("createdDateEnd", params.createdDateEnd);
		if (params.vendorEventStatus) {
			params.vendorEventStatus.forEach((status: number) =>
				queryParams.append("vendorEventStatus", status.toString()),
			);
		}
		if (params.eventType) queryParams.append("eventType", params.eventType);
		if (params.unreadOnly !== undefined) queryParams.append("unreadOnly", params.unreadOnly.toString());
	}

	return queryParams.toString();
}

function buildUnreadCountQueryKey(params?: IGetAllChatsParams): string {
	const queryParams = new URLSearchParams();

	if (params) {
		if (params.search) queryParams.append("search", params.search);
		if (params.orderId) queryParams.append("orderId", params.orderId);
		if (params.eventDateStart) queryParams.append("eventDateStart", params.eventDateStart);
		if (params.eventDateEnd) queryParams.append("eventDateEnd", params.eventDateEnd);
		if (params.createdDateStart) queryParams.append("createdDateStart", params.createdDateStart);
		if (params.createdDateEnd) queryParams.append("createdDateEnd", params.createdDateEnd);
		if (params.vendorEventStatus && params.vendorEventStatus.length > 0) {
			queryParams.append(
				"vendorEventStatus",
				params.vendorEventStatus.map((status: number) => status.toString()).join(","),
			);
		}
		if (params.eventType) queryParams.append("eventType", params.eventType);
		if (params.unreadOnly !== undefined) queryParams.append("unreadOnly", params.unreadOnly.toString());
	}

	return queryParams.toString();
}

const ABORTED = { statusCode: null, message: "Request aborted" };

const latestChatsRecord = createLatestRequest();

export const getAllChatsRecord = createAsyncThunk<IChatsData, IGetAllChatsParams, iThunkApi>(
	"chats/chats-record",
	async (params, thunkAPI) => {
		const queryString = buildChatsListQueryKey(params);
		const { stale, response: resp } = await latestChatsRecord(queryString, () =>
			apiManager(API_ENDPOINTS.GET_ALL_VENDOR_CHATS(queryString), null, API_METHOD_TYPE.GET, {}),
		);
		if (stale || thunkAPI.signal.aborted) return thunkAPI.rejectWithValue(ABORTED);
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data.data;
	},
);

const latestUnreadChannelsCount = createLatestRequest();

export const getUnreadChannelsCount = createAsyncThunk<
	{ totalUnreadCount: number },
	IGetAllChatsParams,
	iThunkApi
>("chats/unread-channels-count", async (params, thunkAPI) => {
	const queryString = buildUnreadCountQueryKey(params);
	const { stale, response: resp } = await latestUnreadChannelsCount(queryString, () =>
		apiManager(API_ENDPOINTS.GET_UNREAD_CHANNELS_COUNT(queryString), null, API_METHOD_TYPE.GET, {}),
	);
	if (stale || thunkAPI.signal.aborted) return thunkAPI.rejectWithValue(ABORTED);
	if (!resp || !isApiSuccess(resp.status)) {
		return thunkAPI.rejectWithValue(resp.data);
	}
	return resp.data.data;
});

export const getAdminVendorChatRecordThunk = createAsyncThunk<
	IAdminVendorChatDetails,
	undefined,
	iThunkApi
>("chats/admin-vendor-chat", async (params, thunkAPI) => {
	// Abort previous request if it exists
	const resp = await apiManager(
		API_ENDPOINTS.GET_ADMIN_VENDOR_CHAT(),
		null,
		API_METHOD_TYPE.GET,
		{},
		undefined,
		false,
		undefined,
	);

	if (!resp || !isApiSuccess(resp.status)) {
		return thunkAPI.rejectWithValue(resp.data);
	}
	return resp.data.data;
});

export const createAdminVendorChat = createAsyncThunk<
  {
    message: string;
    statusCode: number;
  },
  { vendorId: string },
  iThunkApi
>('chats/create-chat', async ({ vendorId }, thunkAPI) => {
  const resp = await apiManager(
    API_ENDPOINTS.CREATE_ADMIN_VENDOR_CHAT(),
    {
      vendorId,
    },
    API_METHOD_TYPE.POST,
    {}
  );
  if (!resp || !isApiSuccess(resp.status)) {
    return thunkAPI.rejectWithValue(resp.data);
  }
  return resp.data.data;
});

// No abort logic here; caller (e.g. ChatsListPage) aborts only when same eventId is requested again.
export const getChatRecordByEventId = createAsyncThunk<
  IChatsData,
  // `listedChatsOnly`: the list is filtered, so only refresh the chats it already shows — this response is unfiltered.
  { eventId: string; signal?: AbortSignal; listedChatsOnly?: boolean },
  iThunkApi
>(
  'chats/chats-record-by-event-id',
  async ({ eventId, signal }, thunkAPI) => {
    if (!eventId) {
      return thunkAPI.rejectWithValue({ statusCode: 400, message: 'eventId is required' });
    }

    const queryString = new URLSearchParams({ eventId }).toString();
    const requestSignal = signal ?? thunkAPI.signal;

    try {
      const resp = await apiManager(
        API_ENDPOINTS.GET_ALL_VENDOR_CHATS(queryString),
        null,
        API_METHOD_TYPE.GET,
        {},
        undefined,
        false,
        undefined,
        requestSignal
      );

      if (!resp || !isApiSuccess(resp.status)) {
        return thunkAPI.rejectWithValue(resp.data);
      }
      return resp.data.data;
    } catch (error: any) {
      if (error.name === 'AbortError' || error.name === 'CanceledError' || requestSignal.aborted) {
        return thunkAPI.rejectWithValue({ statusCode: null, message: 'Request aborted' });
      }
      throw error;
    }
  }
);

const sendChatStateRequest = async (
  url: string,
  body: Record<string, unknown>,
  method: string,
  thunkAPI: { rejectWithValue: (value: iApiError) => unknown }
) => {
  try {
    const resp = await apiManager(url, body, method, {});
    if (!resp || !isApiSuccess(resp.status)) {
      return thunkAPI.rejectWithValue(resp?.data ?? DEFAULT_API_ERROR);
    }
    return resp.data.data;
  } catch (error) {
    return thunkAPI.rejectWithValue((error as AxiosError<iApiError>).response?.data ?? DEFAULT_API_ERROR);
  }
};

export const archiveVendorChats = createAsyncThunk<IArchiveVendorChatsResponse, IVendorChatTarget[], iThunkApi>(
  'chats/archive-chats',
  async (chats, thunkAPI) =>
    sendChatStateRequest(API_ENDPOINTS.ARCHIVE_VENDOR_CHATS(), { chats }, API_METHOD_TYPE.PUT, thunkAPI)
);

export const unarchiveVendorChats = createAsyncThunk<IUnarchiveVendorChatsResponse, IVendorChatTarget[], iThunkApi>(
  'chats/unarchive-chats',
  async (chats, thunkAPI) =>
    sendChatStateRequest(API_ENDPOINTS.UNARCHIVE_VENDOR_CHATS(), { chats }, API_METHOD_TYPE.POST, thunkAPI)
);
